import { PressEvent } from "@react-types/shared";
import { TourElement } from "core/types";
import { Button, ButtonProps } from "ds/ui";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import {
  CheckCircleIcon,
  CircleAlertIcon,
  CircleSlashIcon,
  InfoIcon,
  XIcon,
} from "lucide-react";
import {
  Dispatch,
  ElementType,
  HTMLAttributes,
  SetStateAction,
  useState,
} from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { useTranslations } from "translations";

type BannerVariants = VariantProps<typeof bannerVariants>;
type BannerColor = Exclude<BannerVariants["color"], undefined>;

export type BannerAction = Pick<
  ButtonProps,
  | "as"
  | "href"
  | "isDisabled"
  | "isLoading"
  | "startContent"
  | "endContent"
  | "onPress"
  | "id"
> & {
  tour?: TourElement;
  label: string;
};

export type BannerProps = HTMLAttributes<HTMLDivElement> & {
  actions?: BannerAction[];
  heading?: string;
  message: React.ReactNode | string;
  messageTour?: TourElement;
  noIcon?: boolean;
  onClose?: (e?: PressEvent) => void;
  withClose?: boolean;
  ref?: React.Ref<HTMLDivElement>;
} & BannerVariants;

const DEFAULT_COLOR = "primary";
const ICONS: Record<BannerColor, ElementType> = {
  danger: CircleSlashIcon,
  primary: InfoIcon,
  success: CheckCircleIcon,
  warning: CircleAlertIcon,
};

const bannerVariants = tv({
  defaultVariants: {
    color: DEFAULT_COLOR,
    size: "md",
  },
  slots: {
    bannerActions: "ml-3 flex items-center gap-6 overflow-visible",
    bannerContent: "flex flex-col flex-1 gap-2",
    bannerheading: "font-semibold",
    bannerMessage: "text-body-md",
    icon: "",
    wrapper:
      "flex flex-row rounded-lg border-1 border-solid border-gray-500 p-4 space-between w-full items-center gap-3",
  },
  variants: {
    color: {
      danger: { icon: "text-danger-dark", wrapper: "bg-danger-light" },
      primary: {
        icon: "text-primary",
        wrapper: "bg-primary-light",
      },
      success: {
        icon: "text-success-dark",
        wrapper: "bg-success-light",
      },
      warning: {
        icon: "text-warning-dark",
        wrapper: "bg-warning-light",
      },
    },
    size: {
      md: {
        icon: "h-5 w-5",
      },
      sm: {
        icon: "h-4 w-4",
      },
    },
  },
});

const BannerCloseIcon = ({
  actions,
  onClose,
  setOpen,
  withClose,
}: Pick<BannerProps, "onClose" | "withClose" | "actions"> & {
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const firstFocusableElement = useFocusElement<HTMLButtonElement>({
    dependencies: [withClose],
    shouldFocus: !!withClose && !actions?.length,
  });
  const translations = useTranslations();

  if (!withClose && !onClose) return null;

  return (
    <Button
      ref={firstFocusableElement as any}
      aria-label={translations.actions.close}
      color="black"
      id="banner-close"
      isIconOnly
      size="xs"
      variant="light"
      onPress={(e) => {
        setOpen(false);
        onClose?.(e);
      }}
    >
      <XIcon />
    </Button>
  );
};

const BannerAdditionalActions = ({
  actions,
  color,
  withClose,
}: Pick<BannerProps, "withClose" | "actions" | "color">) => {
  const firstFocusableElement = useFocusElement<HTMLButtonElement>({
    dependencies: [withClose],
    shouldFocus: !!actions,
  });

  if (!actions?.length) return null;

  return (
    <>
      {actions.map(
        (
          {
            as,
            endContent,
            href,
            id,
            isDisabled,
            isLoading,
            label,
            onPress,
            startContent,
            tour,
          },
          index,
        ) => (
          <span key={id} {...tour}>
            <Button
              ref={index === 0 ? (firstFocusableElement as any) : undefined}
              as={as}
              color={color}
              endContent={endContent}
              href={href}
              id={id}
              isDisabled={isDisabled}
              isLoading={isLoading}
              size="sm"
              startContent={startContent}
              variant="solid"
              onPress={onPress}
            >
              {label}
            </Button>
          </span>
        ),
      )}
    </>
  );
};

const BannerActions = ({
  actions,
  className,
  color,
  onClose,
  setOpen,
  withClose,
}: Pick<BannerProps, "actions" | "onClose" | "withClose" | "color"> & {
  className: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  if (!actions?.length && !withClose && !onClose) return null;

  return (
    <div className={className}>
      <BannerAdditionalActions actions={actions} color={color} />
      <BannerCloseIcon
        actions={actions}
        setOpen={setOpen}
        withClose={withClose}
        onClose={onClose}
      />
    </div>
  );
};

const BannerIcon = ({
  className,
  color = DEFAULT_COLOR,
  noIcon,
}: Pick<BannerProps, "noIcon" | "color"> & { className: string }) => {
  const Icon = ICONS[color];

  if (noIcon) return null;

  return <Icon className={className} />;
};

export function Banner({
  actions,
  color = "primary",
  heading,
  message,
  messageTour,
  noIcon,
  onClose,
  ref,
  size,
  withClose,
  ...rest
}: BannerProps) {
  const [open, setOpen] = useState(true);
  const {
    bannerActions,
    bannerContent,
    bannerheading,
    bannerMessage,
    icon,
    wrapper,
  } = bannerVariants({
    color,
    size,
  });

  if (!open) return null;

  const Container = typeof message === "string" ? "p" : "div";

  return (
    <div ref={ref} {...rest} className={wrapper()}>
      <BannerIcon className={icon()} color={color} noIcon={noIcon} />
      <div className={bannerContent()}>
        {heading && <p className={bannerheading()}>{heading}</p>}
        <Container className={bannerMessage()} {...messageTour}>
          {message}
        </Container>
      </div>
      <BannerActions
        actions={actions}
        className={bannerActions()}
        color={color}
        setOpen={setOpen}
        withClose={withClose}
        onClose={onClose}
      />
    </div>
  );
}

Banner.displayName = "RecareUI.Banner";
